import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ExternalLink, Calendar } from 'lucide-react';
import { Button } from '../../components/ui/button';
import Layout from '../../components/layout/Layout';
import SEO from '../../components/SEO';
import { PRODUCTS } from '../../lib/constants';

const fadeInUp = { hidden: { opacity: 0, y: 40 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] } } };
const staggerContainer = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.1 } } };

const ProductCard = ({ product, featured }) => {
  const accent = product.accentColor || '#1B93A4';
  const cardInner = (
    <div className={`glass-card-hover h-full p-8 group flex flex-col ${featured ? 'md:flex-row md:items-center gap-8' : ''}`} style={featured ? { borderColor: `${accent}40` } : undefined}>
      <div className={featured ? 'flex-shrink-0' : ''}>
        <img
          src={product.logo}
          alt={product.name}
          loading="lazy"
          className={`object-cover rounded-2xl ${featured ? 'w-20 h-20' : 'w-14 h-14 mb-6'}`}
          style={featured ? { boxShadow: `0 0 30px ${accent}40` } : undefined}
        />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2">
          <h3 className={`font-bold text-[#F0F4FF] ${featured ? 'text-2xl md:text-3xl' : 'text-xl'}`}>{product.name}</h3>
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold text-white ${product.statusColor}`}>
            {product.status}
          </span>
        </div>
        <p className="text-[#8892A4] text-sm mb-4">{product.descriptionMenu}</p>
        <span className="inline-flex items-center font-medium text-sm group-hover:text-[#F0F4FF] transition-colors" style={{ color: accent }}>
          {product.external ? <>Visitar <ExternalLink className="w-4 h-4 ml-1" /></> : <>Conocer más <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" /></>}
        </span>
      </div>
    </div>
  );

  if (product.external) {
    return (
      <a href={product.href} target="_blank" rel="noopener noreferrer" className={featured ? 'md:col-span-3 block' : 'block'}>
        {cardInner}
      </a>
    );
  }
  return (
    <Link to={product.href} className={featured ? 'md:col-span-3 block' : 'block'}>
      {cardInner}
    </Link>
  );
};

const ProductosHub = () => {
  const featured = PRODUCTS.filter(p => !p.external);
  const external = PRODUCTS.filter(p => p.external);

  return (
    <Layout>
      <SEO path="/productos" />

      {/* Hero */}
      <section className="relative py-24 md:py-32 overflow-hidden" data-testid="productos-hero">
        <div className="absolute inset-0">
          <div className="mesh-gradient opacity-40" />
          <div className="grid-pattern absolute inset-0 opacity-20" />
        </div>
        <div className="container-main relative z-10">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-3xl">
            <motion.span variants={fadeInUp} className="text-[#1B93A4] font-semibold mb-4 block uppercase tracking-wider text-sm" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Nuestros productos</motion.span>
            <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl md:text-6xl font-black text-[#F0F4FF] mb-6 leading-tight">
              Tecnología propia <span className="gradient-text">de Leapifyes</span>
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-lg text-[#8892A4] leading-relaxed max-w-2xl">
              No solo asesoramos: construimos y operamos nuestros propios productos. Software real, en producción, que usa tu mismo tipo de negocio.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Grid de productos */}
      <section className="section-padding border-t border-white/5" data-testid="productos-grid">
        <div className="container-main">
          <div className="grid md:grid-cols-3 gap-6">
            {featured.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="md:col-span-3"
              >
                <ProductCard product={product} featured />
              </motion.div>
            ))}
            {external.map((product, index) => (
              <motion.div
                key={product.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
              >
                <ProductCard product={product} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding relative overflow-hidden" data-testid="productos-cta">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1B93A4]/10 via-transparent to-[#D946EF]/10" />
        <div className="container-main relative z-10">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="glass-card p-12 md:p-16 text-center">
            <h2 className="text-3xl md:text-4xl font-black text-[#F0F4FF] mb-4">¿Necesitas un producto a medida?</h2>
            <p className="text-lg text-[#8892A4] mb-8 max-w-2xl mx-auto">Construimos software propio para nuestros clientes igual que lo hacemos para nosotros. Cuéntanos tu idea.</p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a href="https://ricardoserrano-leapifyes.zohobookings.eu/#/242621000000571012" target="_blank" rel="noopener noreferrer">
                <Button className="btn-gradient text-base px-8 py-6" data-testid="productos-cta-btn">
                  <Calendar className="w-5 h-5 mr-2" /> Agendar diagnóstico gratuito <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
              <Link to="/contacto">
                <Button className="btn-secondary text-base px-8 py-6">Contactar con el equipo</Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default ProductosHub;
